/**
 * cvParser.js
 * --------------------------------------------------------------------------
 * Client-side CV/resume parser. Takes a File (PDF, DOCX or TXT), extracts
 * raw text, and runs regex + dictionary scans to pull out:
 *   - name, email, phone
 *   - skills (matched against ALL_SKILLS aliases, multilingual)
 *   - years of total experience (summed from date ranges)
 *   - inferred current role (best-guess from title patterns)
 *
 * NO data leaves the browser. The CV is parsed in-place. Result lives in
 * localStorage and can be cleared at any time. This is the GDPR-clean path.
 *
 * EU AI Act note: this is NOT generative AI inference. It is deterministic
 * pattern matching. The transparency obligations under Art. 13 are easy to
 * satisfy because we can show the user exactly which alias matched which
 * skill (skillEvidence map below).
 */

import { ALL_SKILLS, ROLES } from '../data/skillOntology';
import { inferSkillsFromTitles } from '../data/jobTitleSkills';
import { logAiEvent } from './aiAuditLog';

// pdfjs-dist 4.x requires GlobalWorkerOptions.workerSrc to be set even when
// you'd rather it run on the main thread, disableWorker isn't reliably
// honoured. We use Vite's ?url asset import to get a proper bundled URL
// for the worker file. This lazy-loads the worker only when a PDF is
// actually uploaded, so initial app load is unaffected.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// ---------------------------------------------------------------------------
// Text extraction (per-format)
// ---------------------------------------------------------------------------

async function extractTextFromPdf(file) {
  const pdfjsLib = await import('pdfjs-dist/build/pdf.mjs');
  // Point pdfjs at the worker URL Vite emitted at build/dev time.
  // Without this, every getDocument() throws "No GlobalWorkerOptions.workerSrc specified".
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  }
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({
    data: arrayBuffer,
    isEvalSupported: false,
    useSystemFonts: true,
  });
  const pdf = await loadingTask.promise;
  const pages = [];
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    let lastY = null;
    let pageText = '';
    for (const item of content.items) {
      const y = item.transform?.[5];
      if (lastY !== null && Math.abs(y - lastY) > 2) {
        pageText += '\n';
      } else if (pageText && !pageText.endsWith(' ')) {
        pageText += ' ';
      }
      pageText += item.str;
      lastY = y;
    }
    pages.push(pageText);
  }
  return pages.join('\n');
}

async function extractTextFromDocx(file) {
  const mammoth = await import('mammoth/mammoth.browser');
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value || '';
}

async function extractTextFromTxt(file) {
  return await file.text();
}

export async function extractText(file) {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.pdf') || file.type === 'application/pdf') {
    return await extractTextFromPdf(file);
  }
  if (name.endsWith('.docx') || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return await extractTextFromDocx(file);
  }
  if (name.endsWith('.txt') || file.type === 'text/plain') {
    return await extractTextFromTxt(file);
  }
  throw new Error('Unsupported file type. Please upload PDF, DOCX, or TXT.');
}

// ---------------------------------------------------------------------------
// Field extractors
// ---------------------------------------------------------------------------

function extractName(text) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 12);
  const noiseWords = /\b(curriculum|vitae|lebenslauf|resume|cv|profile|profil|address|adresse|email|e-mail|telefon|phone|mobile|date|datum|page)\b/i;
  const looksLikeName = (line) => {
    if (line.length > 60 || line.length < 3) return false;
    if (noiseWords.test(line)) return false;
    if (/[@\d]/.test(line)) return false;
    const words = line.split(/\s+/).filter(Boolean);
    if (words.length < 2 || words.length > 5) return false;
    const capRatio = words.filter((w) => /^[A-ZÄÖÜ]/.test(w)).length / words.length;
    return capRatio >= 0.6;
  };
  for (const line of lines) {
    if (looksLikeName(line)) return line;
  }
  return lines[0] || '';
}

function extractEmail(text) {
  const match = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? match[0] : '';
}

function extractPhone(text) {
  const match = text.match(/(\+?\d[\d\s().-]{7,}\d)/);
  return match ? match[0].trim() : '';
}

/**
 * Skill detection, scan against every alias in ALL_SKILLS using word-boundary
 * regex. Multilingual: each skill's `aliases` array contains DE + EN + common
 * synonyms, so a German CV gets the same coverage as an English one.
 */
function extractSkills(text) {
  const lower = text.toLowerCase();
  const matchedSkillIds = new Set();
  const skillEvidence = {};
  for (const skill of ALL_SKILLS) {
    for (const alias of skill.aliases) {
      const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(?:^|[^a-zA-Z0-9_])${escaped}(?:[^a-zA-Z0-9_]|$)`, 'i');
      if (re.test(lower)) {
        matchedSkillIds.add(skill.id);
        if (!skillEvidence[skill.id]) skillEvidence[skill.id] = [];
        skillEvidence[skill.id].push(alias);
      }
    }
  }
  return { matchedSkillIds: Array.from(matchedSkillIds), skillEvidence };
}

function extractYearsOfExperience(text) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const rangeRe = /(?:[A-Za-zäöüÄÖÜ]{3,}\s+)?(\d{2}[\/.])?(\d{4})\s*[-–,]\s*(?:[A-Za-zäöüÄÖÜ]{3,}\s+)?(?:(\d{2}[\/.])?(\d{4})|present|current|heute|jetzt|today|now|aktuell)/gi;
  const ranges = [];
  let m;
  while ((m = rangeRe.exec(text)) !== null) {
    const startYear = parseInt(m[2], 10);
    const endYear = m[4] ? parseInt(m[4], 10) : currentYear;
    if (startYear < 1970 || startYear > currentYear) continue;
    if (endYear < startYear || endYear > currentYear) continue;
    ranges.push([startYear, endYear]);
  }
  if (ranges.length === 0) return 0;
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    if (ranges[i][0] <= last[1]) {
      last[1] = Math.max(last[1], ranges[i][1]);
    } else {
      merged.push(ranges[i]);
    }
  }
  return merged.reduce((sum, [s, e]) => sum + (e - s), 0);
}

function extractCurrentTitle(text) {
  const titlePatterns = [
    /(?:Senior\s+)?Director(?:[,\s]+[A-Z][\w\s]+)?/,
    /(?:Senior|Lead|Principal|Staff)\s+(?:Software|Product|Data|Engineering)\s+(?:Engineer|Manager|Architect)/,
    /(?:Senior\s+)?Vice\s+President(?:[,\s]+[A-Z][\w\s]+)?/,
    /(?:Senior\s+)?(?:Account|Sales|Customer)\s+(?:Executive|Manager|Director)/,
    /(?:Senior\s+)?Product\s+Manager/i,
    /(?:Senior\s+)?HR\s+(?:Business\s+Partner|Manager|Director)/i,
    /Chief\s+\w+\s+Officer/,
    /Head\s+of\s+[\w\s]+/,
    /(?:Senior\s+)?(?:Software|Frontend|Backend|Full[- ]?Stack)\s+(?:Developer|Engineer)/i,
  ];
  const head = text.slice(0, 1500);
  for (const re of titlePatterns) {
    const match = head.match(re);
    if (match) return match[0].trim();
  }
  for (const re of titlePatterns) {
    const match = text.match(re);
    if (match) return match[0].trim();
  }
  return '';
}

function inferRole(matchedSkillIds) {
  const matchedSet = new Set(matchedSkillIds);
  let bestRole = null;
  let bestScore = -1;
  for (const roleKey of Object.keys(ROLES)) {
    const role = ROLES[roleKey];
    const overlap = role.requiredSkillIds.filter((id) => matchedSet.has(id)).length;
    if (overlap > bestScore) {
      bestScore = overlap;
      bestRole = roleKey;
    }
  }
  return { roleKey: bestRole, overlap: bestScore };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function parseCv(file) {
  const startTime = Date.now();
  const text = await extractText(file);
  if (!text || text.trim().length < 50) {
    throw new Error('Could not extract enough text from this file. Please try a different format.');
  }

  const name = extractName(text);
  const email = extractEmail(text);
  const phone = extractPhone(text);
  const { matchedSkillIds: keywordSkillIds, skillEvidence } = extractSkills(text);

  // Title inference: infer skills from job titles mentioned anywhere in
  // the CV. Catches skills that the keyword scan misses because the user
  // demonstrates the skill (by holding the title) without naming it.
  const { inferredSkillIds: titleSkillIds, matchedTitles } = inferSkillsFromTitles(text);

  // Union of keyword detection and title inference. Order matters for
  // consistent output but Set handles dedup.
  const detectedSet = new Set([...keywordSkillIds, ...titleSkillIds]);
  const detectedSkillIds = Array.from(detectedSet);

  const yearsExperience = extractYearsOfExperience(text);
  const currentTitle = extractCurrentTitle(text);
  const { roleKey: inferredRoleKey } = inferRole(detectedSkillIds);

  const result = {
    schemaVersion: 2,
    parsedAt: new Date().toISOString(),
    parseDurationMs: Date.now() - startTime,
    source: 'cv',
    sourceProvider: file.name,
    filename: file.name,
    fileSize: file.size,
    name,
    email,
    phone,
    currentTitle,
    yearsExperience,
    detectedSkillIds,
    keywordDetectedSkillIds: Array.from(keywordSkillIds),
    titleInferredSkillIds: Array.from(titleSkillIds),
    matchedTitles,
    skillEvidence,
    inferredRoleKey,
    rawTextLength: text.length,
    // Retain raw CV text so the optional LLM enricher can use it. Stays
    // entirely client-side; never sent to ClearEqual servers. Only sent
    // to Anthropic IF the user has opted into LLM enrichment via env.
    rawText: text,
  };

  // EU AI Act Art. 12, record-keeping. Logs are kept in localStorage for
  // the demo; in production they'd ship to immutable server-side storage.
  logAiEvent({
    operation: 'cv_parse',
    filename: file.name,
    fileSize: file.size,
    keywordSkillsDetected: keywordSkillIds.length,
    titleInferredSkills: titleSkillIds.size,
    skillsDetectedTotal: detectedSkillIds.length,
    matchedTitleCount: matchedTitles.length,
    yearsExperience,
    inferredRoleKey,
    parseDurationMs: result.parseDurationMs,
  });

  return result;
}
